import i18n from "./i18n/i18n"

// Sentry's feedback form has no custom fields, so the priority is added from outside: a select put
// into the form each time it opens, read back onto the feedback event it sends, in two places:
//
// - a `priority` tag, so it can be filtered on in Sentry (`priority:high`);
// - the start of the message, because a row of Sentry's feedback list shows no tags, only the message
//   on a single line. Line breaks collapse there, so the brackets keep it apart from the text.
//
// This leans on the SDK's markup (the `#sentry-feedback` host, `dialog[open]`, the message label and
// its `form__*` classes). If an SDK update changes it, the select is simply not added and feedback
// arrives without a priority — worth checking after bumping @sentry/vue.

// Tag values stay English whatever the UI language, so one search finds every report.
const PRIORITIES = ["low", "normal", "high", "critical"]
const DEFAULT_PRIORITY = "normal"

// The integration's shadow root holds every form created so far; a closed one stays mounted until it
// is submitted, so only the open dialog is the one on screen.
export const openForm = () => document.getElementById("sentry-feedback")?.shadowRoot?.querySelector("dialog[open] form")

// `feedbackIntegration({ onFormOpen })`: runs right after the form has rendered.
export function addPrioritySelect() {
  const form = openForm()
  const messageLabel = form?.querySelector("label[for='message']")
  // A form reopened after Abbrechen still has its select, and keeps the choice like it keeps the text.
  if (!messageLabel || form.querySelector("select[name='priority']")) return

  const label = document.createElement("label")
  label.className = "form__label"
  const text = document.createElement("span")
  text.className = "form__label__text"
  text.textContent = i18n.global.t("general.sentry.priorityLabel")
  const select = document.createElement("select")
  select.className = "form__input"
  select.name = "priority"
  for (const priority of PRIORITIES) {
    const selected = priority === DEFAULT_PRIORITY
    select.add(new Option(i18n.global.t(`general.sentry.priorities.${priority}`), priority, selected, selected))
  }
  label.append(text, select)
  // Directly under the description. Preact only moves nodes it re-creates, and the siblings here
  // (description, screenshot button) live as long as the form, so its re-renders leave this in place.
  messageLabel.after(label)
}

// `client.on("beforeSendFeedback")`: fires synchronously while the form is still open and submitting.
// Set on the event, not via the integration's `tags` option — the SDK copies those onto the current
// scope, where they would stick to every error reported afterwards. Only the event's copy of the message
// changes: a form that comes back after a failed send still holds the person's own text, so sending it
// again does not stack a second line.
export function markPriority(event) {
  const priority = openForm()?.querySelector("select[name='priority']")?.value
  if (!priority) return
  event.tags = { ...event.tags, priority }
  const feedback = event.contexts?.feedback
  if (feedback) feedback.message = `[Priority: ${priority}]\n\n${feedback.message}`
}
